// Terrain: procedural ISLAND heightmap + displaced mesh with cartoon vertex-color shading.
// Heights live in a Float32Array so terraforming can edit + re-upload.
// The island interior is deliberately FLAT (slope << buildable threshold) so habitats
// and amenities can always be built; hills are gentle decorative mounds.
import * as THREE from 'three';
import { makeNoise } from './noise.js';
import { MAP_SIZES } from '../core/store.js';

const SQRT1_2 = Math.SQRT1_2;

export class Terrain {
  constructor(sizeKey, seed = Math.floor(Math.random() * 1e9), savedHeights = null, savedWaterLevel = null) {
    const cfg = MAP_SIZES[sizeKey];
    this.cells = cfg.cells;              // grid cells per side
    this.worldSize = cfg.worldSize;      // meters per side
    this.cellSize = this.worldSize / this.cells;
    this.verts = this.cells + 1;         // height samples per side
    this.heights = new Float32Array(this.verts * this.verts);
    this.seed = seed;
    this.maxHeight = 10;
    this.waterLevel = 0;                 // sea level — fixed for the island

    // Island frame: the perimeter fence is an octagon inset from the map edge;
    // the coastline sits OUTSIDE it, so the fence (and everything inside) is always on land.
    this.perimeterInset = Math.max(6, Math.round(20 / this.cellSize)); // cells from map edge to fence
    this.cornerCut = Math.max(8, Math.round(28 / this.cellSize));      // staircase corner cut, in cells

    if (savedHeights) {
      this.heights.set(savedHeights);
      this.waterLevel = savedWaterLevel ?? 0;
    } else {
      this._generate();
    }
    this._buildMesh();
  }

  // Signed distance (meters) to the perimeter-fence octagon. Negative = inside the zoo grounds.
  islandSDF(wx, wz) {
    const F = this.worldSize / 2 - this.perimeterInset * this.cellSize;
    const C = this.cornerCut * this.cellSize;
    const ax = Math.abs(wx), az = Math.abs(wz);
    return Math.max(ax - F, az - F, (ax + az - (2 * F - C)) * SQRT1_2);
  }

  _generate() {
    const n = makeNoise(this.seed);
    const v = this.verts;
    const half = this.worldSize / 2;
    const LAND_PAD = 7;      // grass continues this far outside the fence
    const BEACH_W = 7;       // then beach
    const OCEAN_FALL = 16;   // beach -> deep ocean distance

    // Decorative mounds + one shallow pond, seeded but kept away from the south gate area.
    const rnd = (() => { let s = this.seed ^ 0x2f6e2b1; return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296; })();
    const mounds = [];
    for (let i = 0; i < 6; i++) {
      mounds.push({
        x: (rnd() - 0.5) * this.worldSize * 0.6,
        z: (rnd() - 0.6) * this.worldSize * 0.55,  // bias north, away from gate
        r: 22 + rnd() * 26,
        h: 2.2 + rnd() * 2.4,
      });
    }
    // Lakes: one per quadrant (~10% of the island under water, incl. the pond)
    const lakes = [];
    for (let i = 0; i < 4; i++) {
      const qx = i % 2 ? 1 : -1, qz = i < 2 ? -1 : 1;
      lakes.push({
        x: qx * this.worldSize * (0.12 + rnd() * 0.16),
        z: qz * this.worldSize * (0.10 + rnd() * 0.16),
        r: this.worldSize * (0.055 + rnd() * 0.04),
        d: 2.4 + rnd() * 0.8,
      });
    }
    this.lakes = lakes;
    const pond = {
      x: (rnd() < 0.5 ? -1 : 1) * (this.worldSize * (0.14 + rnd() * 0.14)),
      z: -this.worldSize * (0.05 + rnd() * 0.2),
      r: 9 + rnd() * 5,
    };
    this.pond = pond;

    const gateWz = half - this.perimeterInset * this.cellSize; // gate sits on the south fence line (z+)

    for (let z = 0; z < v; z++) {
      for (let x = 0; x < v; x++) {
        const wx = x * this.cellSize - half;
        const wz = z * this.cellSize - half;
        const nx = x / v, nz = z / v;

        // Rolling interior with real relief — still comfortably under the buildable slope.
        let h = 1.8
          + n.fbm(nx * 1.6 + 3, nz * 1.6 + 3, 3) * 1.3
          + n.fbm(nx * 3.5, nz * 3.5, 4) * 1.5
          + n.fbm(nx * 9 + 13, nz * 9 + 13, 3) * 0.5;
        for (const m of mounds) {
          const d2 = ((wx - m.x) ** 2 + (wz - m.z) ** 2) / (m.r * m.r);
          h += m.h * Math.exp(-d2 * 1.6);
        }
        // Lakes (below sea level)
        for (const lk of lakes) {
          const d2 = ((wx - lk.x) ** 2 + (wz - lk.z) ** 2) / (lk.r * lk.r);
          h -= (lk.d + 1.6) * Math.exp(-d2 * 1.3);
        }
        // Shallow decorative pond (dips just below sea level)
        {
          const d2 = ((wx - pond.x) ** 2 + (wz - pond.z) ** 2) / (pond.r * pond.r);
          h -= 2.3 * Math.exp(-d2 * 1.4);
        }
        // Perfectly calm, flat apron around the entrance gate
        {
          const d2 = (wx * wx + (wz - gateWz) ** 2) / (18 * 18);
          const w = Math.exp(-d2 * 1.2);
          h = h * (1 - w) + 1.4 * w;
        }

        // Coast: distance to the fence octagon decides land / beach / ocean.
        const d = this.islandSDF(wx, wz);
        const coastN = (n.fbm(nx * 5 + 21, nz * 5 + 21, 3) + 1) * 0.5 * 5 - 1; // -1..+4 m, mostly outward
        const beachStart = LAND_PAD + Math.max(-1.5, coastN * 0.4);
        const beachEnd = beachStart + BEACH_W + Math.max(0, coastN);
        if (d > beachStart) {
          if (d < beachEnd) {
            // beach: blend interior height down to just above sea level
            const t = (d - beachStart) / (beachEnd - beachStart);
            const k = t * t * (3 - 2 * t);
            h = h * (1 - k) + 0.55 * k;
          } else {
            // ocean: gentle underwater slope, then deep
            const t = Math.min(1, (d - beachEnd) / OCEAN_FALL);
            const k = t * t * (3 - 2 * t);
            h = 0.55 * (1 - k) + (-6.5) * k;
          }
        }
        this.heights[z * v + x] = h;
      }
    }
  }

  heightAt(x, z) { // vertex indices
    return this.heights[z * this.verts + x];
  }

  // World-space height via bilinear interpolation.
  heightAtWorld(wx, wz) {
    const fx = (wx + this.worldSize / 2) / this.cellSize;
    const fz = (wz + this.worldSize / 2) / this.cellSize;
    const x0 = Math.max(0, Math.min(this.verts - 2, Math.floor(fx)));
    const z0 = Math.max(0, Math.min(this.verts - 2, Math.floor(fz)));
    const tx = fx - x0, tz = fz - z0;
    const h00 = this.heightAt(x0, z0), h10 = this.heightAt(x0 + 1, z0);
    const h01 = this.heightAt(x0, z0 + 1), h11 = this.heightAt(x0 + 1, z0 + 1);
    return (h00 * (1 - tx) + h10 * tx) * (1 - tz) + (h01 * (1 - tx) + h11 * tx) * tz;
  }

  // Max slope (rise over run) within a grid cell — used for buildability.
  cellSlope(cx, cz) {
    const h = [this.heightAt(cx, cz), this.heightAt(cx + 1, cz), this.heightAt(cx, cz + 1), this.heightAt(cx + 1, cz + 1)];
    return (Math.max(...h) - Math.min(...h)) / this.cellSize;
  }

  _buildMesh() {
    const geo = new THREE.PlaneGeometry(this.worldSize, this.worldSize, this.cells, this.cells);
    geo.rotateX(-Math.PI / 2);
    this.geometry = geo;

    // Height texture for the water shader (shore foam + depth tint). Updated on terraform.
    this.heightTexSize = 192;
    this.heightTexSpan = this.worldSize * 0.62; // half-span in meters (a bit beyond the map edge)
    this.heightTexData = new Float32Array(this.heightTexSize * this.heightTexSize);
    this.heightTex = new THREE.DataTexture(this.heightTexData, this.heightTexSize, this.heightTexSize, THREE.RedFormat, THREE.FloatType);
    this.heightTex.magFilter = THREE.LinearFilter;
    this.heightTex.minFilter = THREE.LinearFilter;

    this._applyHeights();

    // Cartoon material: flat-shaded vertex colors. The only shader patch is the
    // path "dirt verge" mask so grass fades softly under paths.
    this.pathMaskSize = 256;
    this.pathMaskData = new Uint8Array(this.pathMaskSize * this.pathMaskSize);
    this.pathMaskTex = new THREE.DataTexture(this.pathMaskData, this.pathMaskSize, this.pathMaskSize, THREE.RedFormat);
    this.pathMaskTex.magFilter = THREE.LinearFilter;
    this.pathMaskTex.minFilter = THREE.LinearFilter;

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      roughness: 0.95,
      metalness: 0.0,
    });
    const self = this;
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uPathMask = { value: self.pathMaskTex };
      shader.uniforms.uWorldSize = { value: self.worldSize };
      mat.userData.shader = shader;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvWPos = transformed;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D uPathMask;\nuniform float uWorldSize;\nvarying vec3 vWPos;')
        .replace('#include <color_fragment>', `#include <color_fragment>
          float pm = texture2D(uPathMask, vWPos.xz / uWorldSize + 0.5).r;
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.82, 0.72, 0.55), smoothstep(0.10, 0.55, pm) * 0.65);
        `);
    };
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.receiveShadow = true;
    this.mesh.castShadow = false;
    this.mesh.name = 'terrain';
  }

  // Write heights + recompute normals, vertex colors and the water height texture.
  _applyHeights() {
    const pos = this.geometry.attributes.position;
    const v = this.verts;
    for (let z = 0; z < v; z++) {
      for (let x = 0; x < v; x++) {
        pos.setY(z * v + x, this.heights[z * v + x]);
      }
    }
    pos.needsUpdate = true;
    this.geometry.computeVertexNormals();
    this._paintColors();
    this.geometry.computeBoundingSphere();
    this._updateHeightTex();
  }

  _updateHeightTex() {
    const S = this.heightTexSize, span = this.heightTexSpan;
    const half = this.worldSize / 2;
    for (let j = 0; j < S; j++) {
      for (let i = 0; i < S; i++) {
        const wx = (i / (S - 1) - 0.5) * 2 * span;
        const wz = (j / (S - 1) - 0.5) * 2 * span;
        let h;
        if (Math.abs(wx) >= half - 0.5 || Math.abs(wz) >= half - 0.5) h = -6.5;
        else h = this.heightAtWorld(wx, wz);
        this.heightTexData[j * S + i] = h;
      }
    }
    this.heightTex.needsUpdate = true;
  }

  _paintColors() {
    const v = this.verts;
    const count = v * v;
    if (!this.geometry.attributes.color) {
      this.geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(count * 3), 3));
    }
    const colors = this.geometry.attributes.color;
    const n = makeNoise(this.seed + 7);
    const grassA = new THREE.Color(0xaede90);   // light pastel grass
    const grassB = new THREE.Color(0x8fcf74);   // deeper grass
    const sandDry = new THREE.Color(0xf4e4b8);
    const sandWet = new THREE.Color(0xdcc394);
    const dirt = new THREE.Color(0xcdb598);     // exposed slopes (player terraform)
    const tmp = new THREE.Color();
    const e = this.cellSize;
    for (let z = 0; z < v; z++) {
      for (let x = 0; x < v; x++) {
        const i = z * v + x;
        const h = this.heights[i];
        const wx = x * this.cellSize - this.worldSize / 2;
        const wz = z * this.cellSize - this.worldSize / 2;
        // local slope for dirt reveal
        const hx = this.heightAt(Math.min(v - 1, x + 1), z) - this.heightAt(Math.max(0, x - 1), z);
        const hz = this.heightAt(x, Math.min(v - 1, z + 1)) - this.heightAt(x, Math.max(0, z - 1));
        const slope = Math.hypot(hx, hz) / (2 * e);

        const macro = n.fbm(x / v * 6, z / v * 6, 4);          // large color patches
        const micro = n.fbm(x / v * 30 + 50, z / v * 30 + 50, 2); // per-vertex jitter

        if (h < 0.75) {
          // beach / underwater sand
          const wet = Math.max(0, Math.min(1, (0.55 - h) / 1.1));
          tmp.copy(sandDry).lerp(sandWet, wet);
          if (h < -1.2) tmp.multiplyScalar(0.86); // deep floor slightly darker
        } else if (h < 1.05) {
          const t = (h - 0.75) / 0.3;
          tmp.copy(sandDry).lerp(grassA, t * t * (3 - 2 * t));
        } else {
          const t2 = Math.max(0, Math.min(1, (macro + 1) / 2));
          tmp.copy(grassA).lerp(grassB, t2);
          tmp.offsetHSL(micro * 0.02, 0, micro * 0.035);
        }
        if (slope > 0.38) {
          const k = Math.min(1, (slope - 0.38) / 0.5);
          tmp.lerp(dirt, k);
        }
        colors.setXYZ(i, tmp.r, tmp.g, tmp.b);
      }
    }
    colors.needsUpdate = true;
  }

  // Update the grass-kill mask from the path cell set (soft blur halo).
  updatePathMask(paths) {
    const S = this.pathMaskSize;
    this.pathMaskData.fill(0);
    const cellsPerTexel = this.cells / S;
    paths.forEach((key) => {
      const [cx, cz] = key.split(',').map(Number);
      const tx = Math.floor((cx + 0.5) / cellsPerTexel);
      const tz = Math.floor((cz + 0.5) / cellsPerTexel);
      const R = Math.max(2, Math.round(1.2 / cellsPerTexel));
      for (let dz = -R; dz <= R; dz++) {
        for (let dx = -R; dx <= R; dx++) {
          const x = tx + dx, z = tz + dz;
          if (x < 0 || z < 0 || x >= S || z >= S) continue;
          const d = Math.sqrt(dx * dx + dz * dz) / R;
          if (d > 1) continue;
          const i = z * S + x;
          const val = Math.round((1 - d) * 255);
          if (val > this.pathMaskData[i]) this.pathMaskData[i] = val;
        }
      }
    });
    this.pathMaskTex.needsUpdate = true;
  }

  refresh() { this._applyHeights(); }
}
